import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Box,
  Typography,
  Input,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  FormLabel,
  Select,
  Option,
  Divider,
  Grid
} from '@mui/joy';
import { MagnifyingGlass as Search, Plus, Envelope as Mail, Phone, UserPlus } from '@phosphor-icons/react';
import { useContacts } from '../../hooks/useCrm';
import type { ContactListItem } from '../../types/crm';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';

interface ContactSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onContactSelect: (contactId: number) => void;
  existingContactIds?: number[];
}

export const ContactSelectionModal: React.FC<ContactSelectionModalProps> = ({
  open,
  onClose,
  onContactSelect,
  existingContactIds = []
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContactType, setSelectedContactType] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const queryClient = useQueryClient();

  // New contact form state
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    contact_type: 'lead' as const
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedContactType('');
      setShowCreateForm(false);
      setNewContact({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        contact_type: 'lead'
      });
    }
  }, [open]);

  // Fetch contacts with filters
  const { data: contactsData, isLoading, error } = useContacts({
    search: searchQuery,
    contact_type: selectedContactType ? [selectedContactType as any] : undefined
  });
  
  // Extract contacts from paginated response
  const contacts = contactsData?.results || [];
  console.log('[ContactSelectionModal] Contacts data:', {
    count: contactsData?.count ?? 0,
    resultsLength: contacts.length
  });

  // Filter out already associated contacts
  const availableContacts = contacts.filter(
    contact => !existingContactIds.includes(contact.id)
  );

  // Create contact mutation
  const createContactMutation = useMutation({
    mutationFn: async (contactData: typeof newContact) => {
      const response = await api.post('/api/crm/contacts/', contactData);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to create contact');
      }

      return response.data;
    },
    onSuccess: (data: any) => {
      // Invalidate contacts query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // Select the newly created contact
      onContactSelect(data.id);
      onClose();
    },
  });

  const handleCreateContact = () => {
    if (!newContact.first_name || !newContact.last_name) {
      return;
    }
    createContactMutation.mutate(newContact);
  };

  const handleSelectContact = (contactId: number) => {
    onContactSelect(contactId);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 9999 }}>
      <ModalDialog size="lg" sx={{ width: '90vw', maxWidth: 800, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2 }}>
          {showCreateForm ? 'Create New Contact' : 'Select Contact'}
        </Typography>

        {!showCreateForm ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Search and Filter Section */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid xs={12} sm={8}>
                  <FormControl>
                    <FormLabel>Search Contacts</FormLabel>
                    <Input
                      placeholder="Search by name, email, or phone..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      startDecorator={<Search />}
                      sx={{ width: '100%' }}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={4}>
                  <FormControl>
                    <FormLabel>Contact Type</FormLabel>
                    <Select
                      placeholder="All types"
                      value={selectedContactType}
                      onChange={(_, value) => setSelectedContactType(value || '')}
                    >
                      <Option value="">All types</Option>
                      <Option value="lead">Lead</Option>
                      <Option value="prospect">Prospect</Option>
                      <Option value="customer">Customer</Option>
                      <Option value="past_customer">Past Customer</Option>
                      <Option value="vendor">Vendor</Option>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Button
                variant="outlined"
                color="primary"
                startDecorator={<UserPlus />}
                onClick={() => setShowCreateForm(true)}
                sx={{ mb: 2 }}
              >
                Create New Contact
              </Button>
            </Box>

            {/* Contacts List */}
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert color="danger" sx={{ mb: 2 }}>
                  Error loading contacts. Please try again.
                </Alert>
              ) : availableContacts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                    {searchQuery ? 'No contacts found matching your search.' : 'No available contacts to add.'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {availableContacts.map((contact: ContactListItem) => (
                    <Card
                      key={contact.id}
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'background.level1' }
                      }}
                      onClick={() => handleSelectContact(contact.id)}
                    >
                      <CardContent sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography level="title-md" sx={{ mb: 0.5 }}>
                              {contact.full_name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                              {contact.email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Mail size={14} />
                                  <Typography level="body-sm">{contact.email}</Typography>
                                </Box>
                              )}
                              {contact.phone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Phone size={14} />
                                  <Typography level="body-sm">{contact.phone}</Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip size="sm" variant="soft" color={
                              contact.contact_type === 'customer' ? 'success' :
                              contact.contact_type === 'lead' ? 'primary' :
                              contact.contact_type === 'prospect' ? 'warning' : 'neutral'
                            }>
                              {contact.contact_type}
                            </Chip>
                            <Button size="sm" variant="solid">
                              Select
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          /* Create Contact Form */
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={newContact.first_name}
                    onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                    placeholder="John"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={newContact.last_name}
                    onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                    placeholder="Doe"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    placeholder="john.doe@example.com"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Phone</FormLabel>
                  <Input
                    type="tel"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <FormControl>
                  <FormLabel>Contact Type</FormLabel>
                  <Select
                    value={newContact.contact_type}
                    onChange={(_, value) => setNewContact({ ...newContact, contact_type: value as any })}
                    slotProps={{ listbox: { sx: { zIndex: 10000 } } }}
                  >
                    <Option value="lead">Lead</Option>
                    <Option value="prospect">Prospect</Option>
                    <Option value="customer">Customer</Option>
                    <Option value="past_customer">Past Customer</Option>
                    <Option value="vendor">Vendor</Option>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => setShowCreateForm(false)}
              >
                Back to Search
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={handleCreateContact}
                loading={createContactMutation.isPending}
                disabled={!newContact.first_name || !newContact.last_name}
                startDecorator={<Plus />}
              >
                Create Contact
              </Button>
            </Box>
          </Box>
        )}
      </ModalDialog>
    </Modal>
  );
};